import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { Panel, Loading, Empty, Field, Th, Td, TableWrap, Money, NativeSelect, Kpi } from "@/components/kit";
import { errMsg } from "@/lib/format";

export const Cnss = () => {
  const { api, t, download } = useApp();
  const [y, setY] = useState(new Date().getFullYear());
  const [q, setQ] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/payroll/cnss/${y}/${q}`).then((r) => setD(r.data)); }, [api, y, q]);
  return (
    <Panel title={t("pay_cnss_decl")} testid="payroll-cnss" action={<div className="flex gap-2">
      <Input type="number" value={y} onChange={(e) => setY(+e.target.value)} className="h-8 w-24" data-testid="cnss-year" />
      <NativeSelect value={q} onChange={(e) => setQ(+e.target.value)} data-testid="cnss-quarter">{[1, 2, 3, 4].map((x) => <option key={x} value={x}>T{x}</option>)}</NativeSelect>
      <Button size="sm" variant="outline" onClick={() => download(`/payroll/cnss/${y}/${q}/export.xlsx`, `cnss-T${q}-${y}.xlsx`, false)} data-testid="cnss-export"><FileSpreadsheet className="h-4 w-4 me-1" />Excel</Button></div>}>
      {!d ? <Loading /> : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-4">
            <Kpi label={t("pay_cnss_base")} value={<Money v={d.total_base} />} testid="cnss-kpi-base" />
            <Kpi label={t("pay_cnss_employee")} value={<Money v={d.employee_part} />} accent="navy" />
            <Kpi label={t("pay_employer_part")} value={<Money v={d.employer_part + d.accident_part} />} accent="navy" />
            <Kpi label={`${t("tax_due")} (${d.rate} %)`} value={<Money v={d.total_due} />} accent="orange" testid="cnss-kpi-due" />
          </div>
          <p className="text-xs text-slate-500">{t("pay_cnss_months")} : {d.validated_months.join(", ") || "—"}</p>
          {d.rows.length === 0 ? <Empty text={t("pay_cnss_empty")} /> : (
            <TableWrap>
              <thead className="border-b"><tr><Th>N° CNSS</Th><Th>CIN</Th><Th>{t("emp_name")}</Th>{d.months.map((m) => <Th key={m} className="text-end">{m}</Th>)}<Th className="text-end">{t("total")}</Th></tr></thead>
              <tbody>{d.rows.map((r) => (
                <tr key={r.cin} className="border-b border-slate-50" data-testid={`cnss-row-${r.code}`}><Td className="num text-xs">{r.cnss_number}</Td><Td className="num text-xs">{r.cin}</Td><Td>{r.name}</Td>
                  {r.months.map((v, i) => <Td key={i} className="text-end"><Money v={v} /></Td>)}<Td className="text-end font-semibold"><Money v={r.total} /></Td></tr>))}</tbody>
            </TableWrap>
          )}
        </div>
      )}
    </Panel>
  );
};

const RATES = ["cnss_employee", "cnss_employer", "accident_rate", "css_rate", "prof_expenses_rate", "prof_expenses_cap", "head_of_family", "per_child", "max_children"];

export const PayrollSettings = () => {
  const { api, t, can } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/payroll/settings").then((r) => setS(r.data)); }, [api]);
  if (!s) return <Loading />;
  const rw = can("payroll", true);
  const save = async () => {
    try { setS((await api.put("/payroll/settings", { ...s, ...Object.fromEntries(RATES.map((k) => [k, +s[k]])), brackets: s.brackets.map(([a, b], i) => [i === s.brackets.length - 1 ? null : +a, +b]) })).data); toast.success(t("saved")); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title={t("pay_settings")} testid="payroll-settings">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={t("pay_employer_no")}><Input value={s.employer_cnss_number} disabled={!rw} onChange={(e) => setS({ ...s, employer_cnss_number: e.target.value })} data-testid="ps-employer-no" /></Field>
        {RATES.map((k) => <Field key={k} label={t(`ps_${k}`)}><Input type="number" step="0.01" value={s[k]} disabled={!rw} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`ps-${k}`} /></Field>)}
      </div>
      <div className="mt-5 eyebrow">{t("ps_brackets")}</div>
      <div className="mt-2 grid gap-2 sm:grid-cols-4">
        {s.brackets.map(([top, rate], i) => (
          <div key={i} className="flex items-center gap-1 text-sm">
            <Input value={top ?? "∞"} disabled={!rw || i === s.brackets.length - 1} onChange={(e) => setS({ ...s, brackets: s.brackets.map((b, j) => (j === i ? [e.target.value, b[1]] : b)) })} className="h-8" data-testid={`ps-bracket-top-${i}`} />
            <Input value={rate} disabled={!rw} onChange={(e) => setS({ ...s, brackets: s.brackets.map((b, j) => (j === i ? [b[0], e.target.value] : b)) })} className="h-8 w-16" data-testid={`ps-bracket-rate-${i}`} />%
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">{t("ps_help")}</p>
      {rw && <Button className="mt-3" onClick={save} data-testid="ps-save">{t("save")}</Button>}
    </Panel>
  );
};
